import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Box, Card, Stack, TableContainer, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { useGetLanguageMapsQuery } from '../../../../api/language'
import { getMotivationNamesFailure, useSaveMotivationNamesMutation } from '../../../../api/motivations'
import { InternalListToolbar } from '../../../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import {
  buildMotivationLocalizationRows,
  getUnstoredMotivationLanguages,
  MotivationLocalizationFormValues,
  MotivationLocalizationRow,
} from '../../helpers/motivationLocalizationHelpers'
import { MotivationNamesChange } from '../../types/Motivations'
import { MotivationLocalizationEditDialog } from '../MotivationLocalizationEditDialog/MotivationLocalizationEditDialog'

type CustomProps = {
  onEdit: (row: MotivationLocalizationRow) => void
  onRemove: (row: MotivationLocalizationRow) => void
}

type DialogState = { open: boolean; row?: MotivationLocalizationRow }

export const MotivationLocalizationsTab: FC = () => {
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const [saveMotivationNames, { isLoading: isSaving }] = useSaveMotivationNamesMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<MotivationLocalizationRow>>()

  const rows = useMemo(() => buildMotivationLocalizationRows(languageMapsQuery.data), [languageMapsQuery.data])

  const tableColumns: GRTableColumn<MotivationLocalizationRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:motivations_key'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.key}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.key.toLowerCase(),
        // GRTable compares strings in reverse, so DESC lists keys A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 240 } },
      },
      {
        labelAccessor: t('internal-service-management:motivations_name_en'),
        accessor: ({ row }) => row.names.en,
        sortable: true,
        sortAccessor: ({ row }) => row.names.en?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-service-management:motivations_name_ja'),
        accessor: ({ row }) => row.names.ja,
        sortable: true,
        sortAccessor: ({ row }) => row.names.ja ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: t('internal-service-management:motivations_name_zh'),
        accessor: ({ row }) => row.names.zh,
        sortable: true,
        sortAccessor: ({ row }) => row.names.zh ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      rowActionsColumn<MotivationLocalizationRow, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-service-management:motivations_edit_short'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-service-management:motivations_remove'), onClick: () => customTableProps?.onRemove(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (row) => setDialogState({ open: true, row }),
      onRemove: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:motivations_remove'),
          confirmText: (
            <Stack spacing={2}>
              <Typography>{t('internal-service-management:motivations_remove_confirm', { key: row.key })}</Typography>
              <Alert severity="warning">{t('internal-service-management:motivations_remove_warning')}</Alert>
            </Stack>
          ),
          actionText: t('internal-service-management:motivations_remove'),
          destructiveAction: true,
          data: row,
        }),
    }),
    [t]
  )

  const save = useCallback(
    async (change: MotivationNamesChange) => {
      try {
        const { expected, stored } = await saveMotivationNames(change).unwrap()
        const unstored = getUnstoredMotivationLanguages(expected, stored)
        if (unstored.length) {
          const languages = unstored.map((language) => language.toUpperCase()).join(', ')
          notifyError(t('internal-service-management:motivations_names_not_stored', { languages }))
          return false
        }
        const messageKey = change.type === 'remove' ? 'motivations_localization_removed' : 'motivations_localization_saved'
        notifySuccess(t(`internal-service-management:${messageKey}`, { key: change.key }))
        return true
      } catch (error) {
        const failure = getMotivationNamesFailure(error)
        const failed = failure?.failedLanguages.map((language) => language.toUpperCase()).join(', ')
        const saved = failure?.savedLanguages.map((language) => language.toUpperCase()).join(', ')
        const message = !failure
          ? t('internal-service-management:motivations_names_failed')
          : saved
          ? t('internal-service-management:motivations_names_partially_saved', { failed, saved })
          : t('internal-service-management:motivations_names_failed_languages', { failed })
        notifyError(message)
        return false
      }
    },
    [notifyError, notifySuccess, saveMotivationNames, t]
  )

  const handleSave = async (values: MotivationLocalizationFormValues) => {
    const key = dialogState.row?.key ?? values.key
    if (await save({ type: 'save', key, names: values.names })) setDialogState({ open: false })
  }

  const handleConfirmRemove = (confirmed?: ConfirmDialogData<MotivationLocalizationRow>) => {
    setConfirmDialogData(undefined)
    const row = confirmed?.data
    if (row?.key) save({ type: 'remove', key: row.key })
  }

  return (
    <Box>
      <InternalListToolbar
        add={{
          label: t('internal-service-management:motivations_add_localization'),
          onClick: () => setDialogState({ open: true }),
          disabled: languageMapsQuery.isLoading || languageMapsQuery.isError || isSaving,
        }}
      />

      <QueryStateBoundary
        isLoading={languageMapsQuery.isLoading}
        isError={languageMapsQuery.isError}
        errorText={t('internal-service-management:motivations_localizations_load_failed')}
      >
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={(row) => row.key}
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <MotivationLocalizationEditDialog
        open={dialogState.open}
        row={dialogState.row}
        rows={rows}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmRemove} />
    </Box>
  )
}
